/**
 * SubjectCard — Work count card for a single subject.
 *
 * Displays subject name, task counts (total, critical, overdue),
 * and completion status badge. Click navigates to focus view.
 *
 * DRY: Works for any industry via subject_label from settings.
 * - Healthcare: "Ruth Thompson" (resident)
 * - Dental: "Smith, John" (patient)
 * - Auto Shop: "2019 Honda Civic #42" (vehicle)
 * - Bakery: "Wedding Cake #789" (order)
 */

import { Card, Group, Stack, Text, Badge, ThemeIcon, UnstyledButton } from '@mantine/core';
import { IconUser, IconAlertCircle, IconCheck, IconClock } from '@tabler/icons-react';

export interface TaskCounts {
  total: number;
  critical: number;
  overdue: number;
  completed: number;
}

export interface SubjectCardProps {
  readonly id: string;
  readonly name: string;
  readonly taskCounts: TaskCounts;
  readonly onClick: (id: string) => void;
  readonly avatarUrl?: string;
}

export function SubjectCard({ id, name, taskCounts, onClick, avatarUrl }: SubjectCardProps) {
  const { total, critical, overdue, completed } = taskCounts;
  const pending = total - completed;
  const isComplete = pending === 0 && total > 0;
  const hasCritical = critical > 0;
  const hasOverdue = overdue > 0;

  return (
    <UnstyledButton onClick={() => onClick(id)} style={{ width: '100%' }}>
      <Card
        shadow="sm"
        padding="md"
        radius="md"
        withBorder
        style={{
          cursor: 'pointer',
          borderColor: hasCritical
            ? 'var(--mantine-color-red-5)'
            : hasOverdue
              ? 'var(--mantine-color-orange-5)'
              : isComplete
                ? 'var(--mantine-color-green-5)'
                : undefined,
          borderWidth: hasCritical || hasOverdue || isComplete ? 2 : 1,
        }}
      >
        <Stack gap="xs">
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon
                size="lg"
                radius="xl"
                color={isComplete ? 'green' : hasCritical ? 'red' : 'blue'}
                variant="light"
              >
                {isComplete ? <IconCheck size={18} /> : <IconUser size={18} />}
              </ThemeIcon>
              <Text fw={500} size="sm" lineClamp={1}>
                {name}
              </Text>
            </Group>

            {isComplete && (
              <Badge color="green" variant="filled" size="sm">
                Done
              </Badge>
            )}
          </Group>

          {!isComplete && (
            <Group gap="xs">
              <Badge color="blue" variant="light" size="sm">
                {pending} task{pending !== 1 ? 's' : ''}
              </Badge>
              {hasCritical && (
                <Badge color="red" variant="filled" size="sm" leftSection={<IconAlertCircle size={12} />}>
                  {critical} critical
                </Badge>
              )}
              {hasOverdue && !hasCritical && (
                <Badge color="orange" variant="filled" size="sm" leftSection={<IconClock size={12} />}>
                  {overdue} overdue
                </Badge>
              )}
            </Group>
          )}
        </Stack>
      </Card>
    </UnstyledButton>
  );
}
